import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';

export interface GalleryImage {
  src: string;
  alt: string;
}

@customElement('rftw-iar')
export class RftwIar extends LitElement {
  /** Section small header tag */
  @property({ type: String }) sectionTag = 'A QUIÉN AYUDAMOS';

  /** Main section title */
  @property({ type: String }) title =
    'International Animal Rescue Nosara (IAR)';

  /** Main description paragraph 1 */
  @property({ type: String }) paragraph1 =
    'es un refugio dedicado al rescate, rehabilitación y liberación de vida silvestre en Guanacaste. Reciben animales heridos por atropellos, electrocución en líneas eléctricas, pérdida de hábitat y tráfico ilegal, con especial atención a los monos aulladores, una de las especies más afectadas de la zona.';

  /** Main description paragraph 2 */
  @property({ type: String }) paragraph2 =
    'Su equipo trabaja todos los días en rescates de emergencia, cuidado veterinario, y en la reintroducción de los animales a su hábitat natural una vez recuperados.';

  /** URL/source for the video/image in the placeholder */
  @property({ type: String }) videoSource =
    '/assets/iar-gallery-monkey-rest.jpg';

  /** Text label for the video placeholder */
  @property({ type: String }) videoLabel = 'Reproducir video de IAR Nosara';

  /** List of images for the bottom photo gallery row */
  @property({ type: Array }) galleryImages: GalleryImage[] = [
    {
      src: '/assets/iar-gallery-monkey-rest.jpg',
      alt: 'Rescue monkey resting',
    },
    {
      src: '/assets/iar-gallery-raccoon-feeding.jpg',
      alt: 'Volunteer feeding a baby raccoon',
    },
    {
      src: '/assets/iar-gallery-howler-baby.jpg',
      alt: 'Baby howler monkey being treated',
    },
    {
      src: '/assets/iar-gallery-parrot-care.jpg',
      alt: 'Veterinarian examining a parrot',
    },
  ];

  static styles = css`
    :host {
      display: block;
      width: 100%;
      box-sizing: border-box;
    }

    .iar-container {
      background-color: #e5e0d3;
      padding: 4.5rem 2rem;
      box-sizing: border-box;
    }

    .content-wrapper {
      max-width: 1200px;
      margin: 0 auto;
    }

    /* Multi-column upper layout */
    .upper-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 3rem;
      align-items: center;
      margin-bottom: 3.5rem;
    }

    .info-section {
      max-width: 580px;
    }

    .section-tag {
      font-size: 0.75rem;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: var(--wa-color-orange-50, #d95328);
      margin-bottom: 0.5rem;
    }

    .section-title {
      font-size: clamp(2rem, 4vw, 2.75rem);
      font-weight: 800;
      color: #1b2e23;
      margin: 0 0 1.5rem 0;
      letter-spacing: -0.02em;
      line-height: 1.1;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    .description-group p {
      font-size: 0.98rem;
      line-height: 1.65;
      color: var(--wa-color-text-normal, #4a5568);
      margin: 0 0 1rem 0;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    .description-group p:last-child {
      margin-bottom: 0;
    }

    .description-group a {
      color: #d95328;
      font-weight: 700;
    }

    /* Video Placeholder Styles */
    .video-placeholder {
      aspect-ratio: 16 / 9;
      background-color: #2d2f2c;
      background-image: var(--iar-video-bg, none);
      background-size: cover;
      background-position: center;
      border-radius: 12px;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      position: relative;
      overflow: hidden;
      box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
      border: none;
      width: 100%;
    }

    .play-button {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      border: 2px solid rgba(255, 255, 255, 0.67);
      background-color: rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(4px);
      display: flex;
      justify-content: center;
      align-items: center;
      transition: all 0.2s ease-in-out;
    }

    .play-button:hover {
      background-color: rgba(255, 255, 255, 0.2);
      transform: scale(1.05);
    }

    /* SVG Play Icon */
    .play-icon {
      width: 24px;
      height: 24px;
      fill: #ffffff;
    }

    /* Photo Gallery Row */
    .photo-gallery {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 1rem;
    }

    .gallery-image {
      aspect-ratio: 4 / 3;
      width: 100%;
      border-radius: 12px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
      display: block;
      object-fit: cover;
      object-position: center;
    }

    /* Mobile Responsive Layout */
    @media (max-width: 1000px) {
      .upper-row {
        grid-template-columns: 1fr;
        gap: 2rem;
      }
      .photo-gallery {
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem;
      }
    }

    @media (max-width: 600px) {
      .iar-container {
        padding: 3rem 1.25rem;
      }
      .upper-row {
        margin-bottom: 2.5rem;
      }

      .photo-gallery {
        gap: 0.75rem;
      }
    }
  `;

  private _handleVideoPlay() {
    /** Dispatches custom event for parent component / video modal handling */
    this.dispatchEvent(
      new CustomEvent('iar-video-play', {
        bubbles: true,
        composed: true,
        detail: {
          videoSource: this.videoSource,
        },
      })
    );
  }

  render() {
    return html`
      <section class="iar-container" aria-label="IAR Nosara Information">
        <div class="content-wrapper">
          <div class="upper-row">
            <div class="info-section">
              ${this.sectionTag
                ? html`<div class="section-tag">${this.sectionTag}</div>`
                : ''}

              <h2 class="section-title">${this.title}</h2>

              <div class="description-group">
                ${this.paragraph1
                  ? html`<p>
                      <a
                        href="https://www.iarcostarica.org/en/"
                        target="_blank"
                        rel="noopener noreferrer"
                        >IAR Nosara</a
                      >
                      ${this.paragraph1}
                    </p>`
                  : ''}
                ${this.paragraph2 ? html`<p>${this.paragraph2}</p>` : ''}
              </div>
            </div>

            <button
              class="video-placeholder"
              style="--iar-video-bg: url('${this.videoSource}')"
              @click=${this._handleVideoPlay}
              aria-label=${this.videoLabel}
            >
              <div class="play-button">
                <svg
                  class="play-icon"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M8 5.14v14.28a1 1 0 001.5.86l10-7.14a1 1 0 000-1.72l-10-7.14A1 1 0 008 5.14z"
                  />
                </svg>
              </div>
            </button>
          </div>

          <div class="photo-gallery">
            ${this.galleryImages.map(
              (img) => html`
                <img
                  class="gallery-image"
                  src=${img.src}
                  alt=${img.alt}
                  loading="lazy"
                  decoding="async"
                />
              `
            )}
          </div>
        </div>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rftw-iar': RftwIar;
  }
}

